import { API_BASE_URL } from "@/shared/api/client";
import type { OrderDto } from "@/shared/api/types";
import { formatDayRange, toOrgDateString } from "@/shared/utils/dates";
import { orderKindLabel } from "@/shared/utils/labels";

/**
 * Picking the one order that matters out of the open ones (ADR-0015 §3). Shared rather than kept with the
 * Subscription tab because the shell's banners and the suspended interstitial ask the same two questions:
 * what is waiting to be paid, and what has to be re-issued before it can be.
 */

/**
 * M20 (ADR-0024 decision 3): an SMS recharge is a billing order too, but it buys credits, not the
 * subscription — paying it renews nothing and lifts no suspension. So the questions below ("what keeps the
 * organisation running?") skip it; Settings → SMS is where a waiting recharge is shown and paid.
 */
export function isSubscriptionOrder(order: Pick<OrderDto, "kind">): boolean {
  return order.kind !== "sms_recharge";
}

/** The one order a customer is being asked to pay right now: the oldest still issued. */
export function payableOrder(orders: readonly OrderDto[] | undefined): OrderDto | null {
  const open = (orders ?? []).filter(
    (o) => isSubscriptionOrder(o) && (o.status === "issued" || o.status === "draft"),
  );
  return [...open].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))[0] ?? null;
}

/**
 * An expired renewal is what "Pay now" re-issues. The gateway invoice behind it is gone once the grace
 * period passes, so there is nothing to open — a fresh one has to be raised on the same lines first.
 */
export function reissuableOrder(orders: readonly OrderDto[] | undefined): OrderDto | null {
  return (orders ?? []).find((o) => isSubscriptionOrder(o) && o.status === "expired") ?? null;
}

/**
 * What a charge was FOR, in words: "Renewal · 13 Oct – 13 Nov 2026", "Added mid-period · prorated to
 * 13 Oct 2026" (M11 follow-up §3D). One row of the payments table has to explain itself without the
 * customer opening it, and "Proration · ₹236" explains nothing.
 */
export function orderPurpose(order: OrderDto, tz: string): string {
  const kind = orderKindLabel(order.kind);
  if (order.kind === "proration") {
    return order.periodEnd ? `${kind} · prorated to ${formatDayRange(oneDay(order.periodEnd, tz))}` : kind;
  }
  if (order.periodStart && order.periodEnd) {
    return `${kind} · ${formatDayRange({
      fromDay: toOrgDateString(order.periodStart, tz),
      toDay: toOrgDateString(order.periodEnd, tz),
    })}`;
  }
  return kind;
}

const oneDay = (at: string, tz: string) => {
  const day = toOrgDateString(at, tz);
  return { fromDay: day, toDay: day };
};

/** The versioned prefix `payUrl` carries in fake mode, and the one `API_BASE_URL` already ends with. */
const API_PREFIX = "/api/v1";

/**
 * Where "Pay now" actually sends the customer.
 *
 * In live mode `payUrl` is the gateway's own hosted page and is opened exactly as the api sent it — it is
 * on somebody else's origin and rewriting any part of it would be wrong.
 *
 * In fake mode the "gateway" is the api itself, and the api builds an absolute URL from its own
 * `PUBLIC_BASE_URL` (`http://127.0.0.1:5100/api/v1/billing/fake/pay/<invoiceId>`). The portal usually
 * reaches the api through its own origin instead — the dev proxy on `localhost:5173`, or a shared origin
 * behind Cloudflare — and `127.0.0.1:5100` is a different HOST, so the session cookie does not travel and
 * the page answers 401. Taking the path after `/api/v1` and hanging it off the portal's own `API_BASE_URL`
 * sends the browser to the same handler over the connection it is already authenticated on.
 *
 * Anything that does not look like our own fake-pay URL is returned untouched: a portal that mangles a
 * payment link is worse than one that opens an unexpected one.
 */
export function resolvePayUrl(payUrl: string, mode: "razorpay" | "fake"): string {
  if (mode !== "fake") return payUrl;
  const index = payUrl.indexOf(API_PREFIX);
  if (index === -1) return payUrl;
  return `${API_BASE_URL}${payUrl.slice(index + API_PREFIX.length)}`;
}
